/**
 * Typed client for the BEAM internal (contractor-only) notes surface.
 *
 * Routes (beam-backend notesRouter — mounted behind requireAuth + withTenant, so
 * structurally contractor-only; customers have no app login):
 *   GET    /api/<ownerType>/:id/internal-notes        → NoteRow[] (newest first)
 *   POST   /api/<ownerType>/:id/internal-notes {body}  → NoteRow
 *   DELETE /api/internal-notes/:noteId                 → NoteRow (soft-deleted)
 *
 * where <ownerType> ∈ estimates | invoices | contacts | jobs.
 *
 * These endpoints return raw Prisma `note` rows (snake_case); we type them locally
 * rather than adding a shared DTO. Bearer token via src/api/client. NEVER imports @smartbox/*.
 */

import { get, post, del } from './client';

/** Note owner segment as it appears in the URL (plural). */
export type NoteOwnerType = 'estimates' | 'invoices' | 'contacts' | 'jobs';

/** Raw Prisma note row returned by the notes endpoints. */
export interface NoteRow {
  id: string;
  organization_id: string;
  owner_type: string;
  owner_id: string;
  author_id: string | null;
  body: string;
  is_internal: boolean;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

/** List internal notes for one owner record (newest first). */
export async function listNotes(ownerType: NoteOwnerType, ownerId: string): Promise<NoteRow[]> {
  return get<NoteRow[]>(`/api/${ownerType}/${ownerId}/internal-notes`);
}

/** Add a contractor-only internal note to an owner record. */
export async function addNote(
  ownerType: NoteOwnerType,
  ownerId: string,
  body: string,
): Promise<NoteRow> {
  return post<NoteRow>(`/api/${ownerType}/${ownerId}/internal-notes`, { body });
}

/** Soft-delete an internal note by id. */
export async function deleteNote(noteId: string): Promise<NoteRow> {
  return del<NoteRow>(`/api/internal-notes/${noteId}`);
}
